import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Card, Button, LoadingSpinner } from './ui';

const VersionHistory = ({ jobId }) => {
  const queryClient = useQueryClient();
  const api = useApi();
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [viewerMode, setViewerMode] = useState(null); // 'view', 'diff', 'revert'

  // Fetch versions
  const { data: versionsData, isLoading, error } = useQuery({
    queryKey: ['versions', jobId],
    queryFn: async () => {
      const response = await api.get(`/api/data/${jobId}/versions`);
      return response.data;
    },
    enabled: !!jobId,
  });

  // Revert mutation
  const revertMutation = useMutation({
    mutationFn: async (versionNumber) => {
      const response = await api.post(`/api/data/${jobId}/revert/${versionNumber}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['jobData', jobId]);
      queryClient.invalidateQueries(['versions', jobId]);
      setSelectedVersion(null);
      setViewerMode(null);
    },
  });

  const handleRevert = (version) => {
    if (window.confirm(`Are you sure you want to revert to version ${version.version_number}? This will create a new version with the old data.`)) {
      revertMutation.mutate(version.version_number);
    }
  };

  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setViewerMode('view');
  };

  const handleCompareVersion = (version) => {
    setSelectedVersion(version);
    setViewerMode('diff');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="text-red-600 dark:text-red-400">
          Error loading version history: {error.message}
        </p>
      </Card>
    );
  }

  const versions = versionsData?.versions || [];

  if (versions.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400 mb-2">No version history yet.</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Versions will be created when you save changes in the editor.
          </p>
        </div>
      </Card>
    );
  }

  // If viewing or comparing a specific version
  if (selectedVersion && viewerMode) {
    return (
      <div className="space-y-4">
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-primary dark:text-primary-light">
              {viewerMode === 'view' ? 'Viewing' : 'Comparing'} {selectedVersion.label || `v${selectedVersion.version_number}`}
              {selectedVersion.label && <span className="text-sm font-normal text-gray-500 ml-2">(v{selectedVersion.version_number})</span>}
            </h3>
            <Button size="sm" variant="secondary" onClick={() => { setSelectedVersion(null); setViewerMode(null); }}>
              Back to History
            </Button>
          </div>
        </Card>

        {viewerMode === 'view' && <VersionViewer jobId={jobId} version={selectedVersion} />}
        {viewerMode === 'diff' && <VersionDiff jobId={jobId} version={selectedVersion} />}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card padding="sm">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-primary dark:text-primary-light">Version History</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{versions.length} versions</p>
        </div>
      </Card>

      <div className="space-y-3">
        {versions.map((version) => (
          <VersionCard
            key={version.version_number}
            version={version}
            jobId={jobId}
            api={api}
            queryClient={queryClient}
            onView={() => handleViewVersion(version)}
            onCompare={() => handleCompareVersion(version)}
            onRevert={() => handleRevert(version)}
          />
        ))}
      </div>
    </div>
  );
};

// Version Card Component
const VersionCard = ({ version, jobId, api, queryClient, onView, onCompare, onRevert }) => {
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(version.label || '');
  const inputRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingLabel && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingLabel]);

  // Update label mutation
  const labelMutation = useMutation({
    mutationFn: async (newLabel) => {
      const response = await api.put(
        `/api/data/${jobId}/versions/${version.version_number}/label`,
        { label: newLabel }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['versions', jobId]);
      setIsEditingLabel(false);
    },
    onError: (error) => {
      console.error('Failed to update label:', error);
      setLabelValue(version.label || '');
      setIsEditingLabel(false);
    },
  });

  const handleLabelClick = () => {
    setLabelValue(version.label || '');
    setIsEditingLabel(true);
  };

  const handleLabelSave = () => {
    const trimmedLabel = labelValue.trim();
    if (trimmedLabel !== (version.label || '')) {
      labelMutation.mutate(trimmedLabel || null);
    } else {
      setIsEditingLabel(false);
    }
  };

  const handleLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLabelSave();
    } else if (e.key === 'Escape') {
      setLabelValue(version.label || '');
      setIsEditingLabel(false);
    }
  };

  const summary = version.change_summary || {};
  const isRevert = summary.reverted_from_version !== undefined;
  const isInitial = summary.initial_version === true || version.version_number === 0;

  return (
    <Card padding="sm">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            {isEditingLabel ? (
              <input
                ref={inputRef}
                type="text"
                value={labelValue}
                onChange={(e) => setLabelValue(e.target.value)}
                onBlur={handleLabelSave}
                onKeyDown={handleLabelKeyDown}
                placeholder="Add label..."
                className="text-lg font-bold px-2 py-1 border border-border rounded bg-white dark:bg-gray-800 text-primary dark:text-primary focus:outline-none"
                disabled={labelMutation.isPending}
              />
            ) : (
              <button
                onClick={handleLabelClick}
                className="text-lg font-bold text-primary dark:text-primary hover:opacity-70 cursor-pointer transition-opacity"
                title="Click to edit label"
              >
                {version.label || `v${version.version_number}`}
              </button>
            )}
            {version.label && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                (v{version.version_number})
              </span>
            )}
            {isInitial && (
              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                Original
              </span>
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatDate(version.created_at)}
            </span>
            {version.created_by && (
              <span className="text-sm text-gray-600 dark:text-gray-300">
                by {version.created_by.name || version.created_by.email}
              </span>
            )}
            {isRevert && (
              <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                Reverted from v{summary.reverted_from_version}
              </span>
            )}
          </div>

          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 flex gap-4 flex-wrap">
            {isInitial && (
              <>
                {summary.rows_extracted > 0 && (
                  <span>📄 {summary.rows_extracted} rows extracted</span>
                )}
                {summary.columns_detected > 0 && (
                  <span>📊 {summary.columns_detected} columns detected</span>
                )}
                {summary.images_processed > 0 && (
                  <span>🖼️ {summary.images_processed} images processed</span>
                )}
              </>
            )}
            {!isInitial && summary.cells_updated > 0 && (
              <span>📝 {summary.cells_updated} cells updated</span>
            )}
            {!isInitial && summary.rows_added > 0 && (
              <span>➕ {summary.rows_added} rows added</span>
            )}
            {!isInitial && summary.rows_deleted > 0 && (
              <span>🗑️ {summary.rows_deleted} rows deleted</span>
            )}
            {isRevert && summary.rows_restored > 0 && (
              <span>↩️ {summary.rows_restored} rows restored</span>
            )}
            {!isInitial && !summary.cells_updated && !summary.rows_added && !summary.rows_deleted && !isRevert && (
              <span className="text-gray-400">No change details</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <Button size="sm" variant="primary" onClick={onView}>
            View
          </Button>
          <Button size="sm" variant="secondary" onClick={onCompare}>
            Compare
          </Button>
          <Button size="sm" variant="secondary" onClick={onRevert}>
            Revert
          </Button>
        </div>
      </div>
    </Card>
  );
};

// Version Viewer Component
const VersionViewer = ({ jobId, version }) => {
  const api = useApi();
  const { data, isLoading, error } = useQuery({
    queryKey: ['versionSnapshot', jobId, version.version_number],
    queryFn: async () => {
      const response = await api.get(`/api/data/${jobId}/versions/${version.version_number}`);
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="text-red-600 dark:text-red-400">Error loading version: {error.message}</p>
      </Card>
    );
  }

  const { columns, rows } = data;

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Version {version.version_number} Snapshot
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {rows.length} rows at {new Date(version.created_at).toLocaleString()}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((row) => (
              <tr key={row.id}>
                {columns.map((column) => (
                  <td
                    key={column}
                    className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100"
                  >
                    {row[column] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// Version Diff Component
const VersionDiff = ({ jobId, version }) => {
  const api = useApi();
  const { data, isLoading, error } = useQuery({
    queryKey: ['versionDiff', jobId, version.version_number],
    queryFn: async () => {
      const response = await api.get(
        `/api/data/${jobId}/versions/${version.version_number}/diff?compare_to=current`
      );
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="text-red-600 dark:text-red-400">Error loading diff: {error.message}</p>
      </Card>
    );
  }

  const { changes } = data;

  if (changes.length === 0) {
    return (
      <Card>
        <p className="text-center text-gray-600 dark:text-gray-400 py-8">
          No differences between this version and current state.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Differences from Current State
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {changes.length} changes detected
        </p>
      </div>

      <div className="space-y-2">
        {changes.map((change, index) => (
          <div
            key={index}
            className={`p-3 rounded ${
              change.type === 'row_added'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                : change.type === 'row_deleted'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {change.type === 'cell_changed' && (
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Cell Changed: <span className="font-mono">{change.field}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="line-through">{change.old_value || '(empty)'}</span>
                      {' → '}
                      <span className="font-semibold">{change.new_value || '(empty)'}</span>
                    </p>
                  </div>
                )}

                {change.type === 'row_added' && (
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Row Added
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Row ID: {change.row_id}
                    </p>
                  </div>
                )}

                {change.type === 'row_deleted' && (
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Row Deleted
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Row ID: {change.row_id}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default VersionHistory;
